import { batch } from 'react-redux'
import { differenceInYears, parseISO } from 'date-fns'
import { formatDNI } from '@chilecompra/react-kit/utils'
import { deleteCookie, getCookie } from '@chilecompra/react-kit/storages'
import { customizeError } from '@chilecompra/react-kit/errors'
import { makeActionCreator } from '../../config/store/utils'
import { DNI_TEST } from '../../config/settings/constants'
import {
  isLocalEnv,
  REACT_APP_API_BASE_SELLER_URL,
  REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED,
  REACT_APP_FEATURE_FLAG_TAXES_KEY_NATURAL_CHECKS,
  REACT_APP_UNIQUE_PASS_APP
} from '../../config/settings/environment'
import { getUniquePassToken, getUniquePassUserInfo, logoutUniquePass } from '../../services/auth'
import { getIdentity } from '../../services/register'
import { onRegisterErrorThunk, updateRegisterWizardWithBackup } from '../RegisterWizard/RegisterWizard.actions'

export const GET_UNIQUE_PASS_TOKEN = 'GET_UNIQUE_PASS_TOKEN'
export const GET_UNIQUE_PASS_TOKEN_ERROR = 'GET_UNIQUE_PASS_TOKEN_ERROR'
export const GET_UNIQUE_PASS_TOKEN_SUCCESS = 'GET_UNIQUE_PASS_TOKEN_SUCCESS'
export const onGetUniquePassToken = makeActionCreator(GET_UNIQUE_PASS_TOKEN)
export const onGetUniquePassTokenError = makeActionCreator(GET_UNIQUE_PASS_TOKEN_ERROR, 'payload')
export const onGetUniquePassTokenThunk = payload => async dispatch => {
  dispatch(onGetUniquePassToken())

  try {
    const backup = JSON.parse(localStorage.getItem('registerWizardBackup'))
    if (!backup) {
      throw customizeError({ code: 400, reason: 'SOMETHING_WENT_WRONG_ERROR' })
    }
    dispatch(updateRegisterWizardWithBackup(backup))

    const { data } = await getUniquePassToken({ code: payload.code, state: payload.csrfToken }).catch(() => {
      throw customizeError({ code: 400, originalError: backup, reason: 'SOMETHING_WENT_WRONG_ERROR' })
    })

    if (backup?.isSignOperation) {
      dispatch(
        onGetSignerUniquePassInfoThunk({
          token: data.payload.access_token,
          refreshToken: data.payload.refresh_token,
          backup
        })
      )
    } else {
      dispatch(
        onGetUserInfoThunk({ token: data.payload.access_token, refreshToken: data.payload.refresh_token, backup })
      )
    }
  } catch (error) {
    const { originalError: backup, reason } = error.toObject()
    return dispatch(onGetUniquePassTokenError({ backup, reason }))
  }
}

export const GET_USER_INFO = 'GET_USER_INFO'
export const GET_USER_INFO_ERROR = 'GET_USER_INFO_ERROR'
export const GET_USER_INFO_SUCCESS = 'GET_USER_INFO_SUCCESS'
export const onGetUserInfo = makeActionCreator(GET_USER_INFO)
export const onGetUserInfoError = makeActionCreator(GET_USER_INFO_ERROR, 'payload')
export const onGetUserInfoSuccess = makeActionCreator(GET_USER_INFO_SUCCESS, 'payload')
export const onGetUserInfoThunk = payload => async dispatch => {
  dispatch(onGetUserInfo())
  localStorage.removeItem('registerWizardBackup')

  try {
    const { token } = payload
    let dni

    // We get dni from cookie if dni input for test is enabled
    if (
      REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED === true ||
      REACT_APP_FEATURE_FLAG_DNI_TEST_INPUT_ENABLED === 'true'
    ) {
      const dniFromCookie = getCookie('dniTestCookie', { base64: true, json: true })

      if (dniFromCookie) {
        dni = dniFromCookie
        deleteCookie('dniTestCookie')
      }
    } else {
      if (isLocalEnv) {
        // Enabled for local and dev environment
        dni = '17.157.036-0'
      } else {
        // Enabled for production and pre-production environment
        const { data } = await getUniquePassUserInfo({ token })
        const { preferred_username: number, digito_verificador: digit } = data.payload
        dni = formatDNI(number + digit)
      }
    }

    if (DNI_TEST.includes(dni)) {
      return batch(() => {
        dispatch(
          onGetUserInfoSuccess({
            dni,
            firstLastName: 'Iturriaga',
            gender: 2,
            name: 'Marianela Andrea',
            secondLastName: 'Jiménez',
            lastName: 'Iturriaga Jiménez',
            backup: payload.backup
          })
        )
        if (!isLocalEnv) {
          dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
        }
      })
    }

    const response = await getIdentity({ dni: formatDNI(dni, 2) })
    const userInfo = response?.data

    /* Validates if the user is underage */
    const dateOfBirth = parseISO(userInfo.payload.fechaNacimiento)
    if (differenceInYears(new Date(), dateOfBirth) < 18) {
      const errorMessage = 'El usuario es menor de edad'
      return batch(() => {
        dispatch(
          onGetUserInfoError(customizeError({ backup: payload.backup, code: 400, reason: 'UNDERAGE_USER_ERROR' }))
        )
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_1_BIS',
            message: errorMessage
          })
        )
        if (!isLocalEnv) {
          dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
        }
      })
    }

    return batch(() => {
      dispatch(
        onGetUserInfoSuccess({
          dni,
          name: userInfo.payload?.nombres,
          firstLastName: userInfo.payload?.apellidoPaterno,
          secondLastName: userInfo.payload?.apellidoMaterno,
          gender: userInfo.payload?.sexo === 'F' ? 2 : 1,
          lastName: userInfo.payload?.apellidoPaterno + ' ' + userInfo.payload?.apellidoMaterno,
          isUniquePass: 1
        })
      )
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  } catch (error) {
    /* Validates if the user is deceased */
    if (error.code === 400 && error.originalError?.response?.data?.errores?.[0]?.codigo === '400-6') {
      const userInfo = error.originalError.response.data.errores[0].descripcion
      return batch(() => {
        dispatch(
          onGetUserInfoError(customizeError({ backup: payload.backup, code: 400, reason: 'DECEASED_PERSON_ERROR' }))
        )
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_1_BIS',
            message: userInfo
          })
        )
        if (!isLocalEnv) {
          dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
        }
      })
    }

    return batch(() => {
      dispatch(onGetUserInfoError(customizeError({ backup: payload, code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })))
      dispatch(
        onRegisterErrorThunk({
          origin: 'INICIO_PASO_1_BIS',
          message: 'Error de servidor. Por favor intente nuevamente más tarde.'
        })
      )
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  }
}

export const GET_SIGNER_UNIQUE_PASS_INFO = 'GET_SIGNER_UNIQUE_PASS_INFO'
export const GET_SIGNER_UNIQUE_PASS_INFO_ERROR = 'GET_SIGNER_UNIQUE_PASS_INFO_ERROR'
export const GET_SIGNER_UNIQUE_PASS_INFO_SUCCESS = 'GET_SIGNER_UNIQUE_PASS_INFO_SUCCESS'
export const onGetSignerUniquePassInfo = makeActionCreator(GET_SIGNER_UNIQUE_PASS_INFO)
export const onGetSignerUniquePassInfoError = makeActionCreator(GET_SIGNER_UNIQUE_PASS_INFO_ERROR, 'payload')
export const onGetSignerUniquePassInfoSuccess = makeActionCreator(GET_SIGNER_UNIQUE_PASS_INFO_SUCCESS, 'payload')
export const onGetSignerUniquePassInfoThunk = payload => async dispatch => {
  dispatch(onGetSignerUniquePassInfo())
  localStorage.removeItem('registerWizardBackup')

  try {
    const { token } = payload
    let dni
    if (isLocalEnv) {
      // Enabled for local and dev environment
      dni = '17.157.036-0'
    } else {
      // Enabled for production and pre-production environment
      const { data } = await getUniquePassUserInfo({ token })
      const { preferred_username: number, digito_verificador: digit } = data.payload
      dni = formatDNI(number + digit)
    }

    if (
      REACT_APP_FEATURE_FLAG_TAXES_KEY_NATURAL_CHECKS === false ||
      REACT_APP_FEATURE_FLAG_TAXES_KEY_NATURAL_CHECKS === 'false'
    ) {
      if (DNI_TEST.includes(dni)) {
        dni = payload.backup?.signerIdentification.signer.dni
      }
    }

    if (dni !== payload.backup?.signerIdentification.signer.dni) {
      const errorMessage = 'El rut logeado en clave unica no coincide con el firmante'
      return batch(() => {
        dispatch(
          onGetSignerUniquePassInfoError(
            customizeError({ backup: payload.backup, code: 400, reason: 'DNI_NOT_MATCH_ERROR' })
          )
        )
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_5_BIS',
            message: errorMessage
          })
        )
        if (!isLocalEnv) {
          dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
        }
      })
    }

    const response = await getIdentity({ dni: formatDNI(dni, 2) })
    const userInfo = response?.data

    /* Validates if the user is underage */
    const dateOfBirth = parseISO(userInfo.payload.fechaNacimiento)
    if (differenceInYears(new Date(), dateOfBirth) < 18) {
      const errorMessage = 'El usuario es menor de edad'
      return batch(() => {
        dispatch(
          onGetSignerUniquePassInfoError(
            customizeError({ backup: payload.backup, code: 400, reason: 'UNDERAGE_USER_ERROR' })
          )
        )
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_5_BIS',
            message: errorMessage
          })
        )
        if (!isLocalEnv) {
          dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
        }
      })
    }

    return batch(() => {
      dispatch(
        onGetSignerUniquePassInfoSuccess({
          dni,
          name: userInfo.payload?.nombres,
          firstLastName: userInfo.payload?.apellidoPaterno,
          secondLastName: userInfo.payload?.apellidoMaterno,
          gender: userInfo.payload?.sexo === 'F' ? 2 : 1,
          lastName: userInfo.payload?.apellidoPaterno + ' ' + userInfo.payload?.apellidoMaterno
        })
      )
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  } catch (error) {
    /* Validates if the user is deceased */
    if (error.code === 400 && error.originalError?.response?.data?.errores?.[0]?.codigo === '400-6') {
      const userInfo = error.originalError.response.data.errores[0].descripcion
      return batch(() => {
        dispatch(
          onGetSignerUniquePassInfoError(
            customizeError({ backup: payload.backup, code: 400, reason: 'DECEASED_PERSON_ERROR' })
          )
        )
        dispatch(
          onRegisterErrorThunk({
            origin: 'INICIO_PASO_5_BIS',
            message: userInfo
          })
        )
        if (!isLocalEnv) {
          dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
        }
      })
    }
    return batch(() => {
      dispatch(
        onGetSignerUniquePassInfoError(
          customizeError({ backup: payload, code: 500, reason: 'SOMETHING_WENT_WRONG_ERROR' })
        )
      )
      dispatch(
        onRegisterErrorThunk({
          origin: 'INICIO_PASO_5_BIS',
          message: 'Error de servidor. Por favor intente nuevamente más tarde.'
        })
      )
      if (!isLocalEnv) {
        dispatch(onLogoutUniquePassThunk({ refreshToken: payload.refreshToken }))
      }
    })
  }
}

export const LOGOUT_UNIQUE_PASS = 'LOGOUT_UNIQUE_PASS'
export const LOGOUT_UNIQUE_PASS_ERROR = 'LOGOUT_UNIQUE_PASS_ERROR'
export const LOGOUT_UNIQUE_PASS_SUCCESS = 'LOGOUT_UNIQUE_PASS_SUCCESS'
export const onLogoutUniquePass = makeActionCreator(LOGOUT_UNIQUE_PASS)
export const onLogoutUniquePassError = makeActionCreator(LOGOUT_UNIQUE_PASS_ERROR, 'payload')
export const onLogoutUniquePassSuccess = makeActionCreator(LOGOUT_UNIQUE_PASS_SUCCESS, 'payload')
export const onLogoutUniquePassThunk = payload => async dispatch => {
  const { refreshToken } = payload || {}

  dispatch(onLogoutUniquePass())

  try {
    await logoutUniquePass({ refreshToken })

    return dispatch(onLogoutUniquePassSuccess())
  } catch (error) {
    return dispatch(onLogoutUniquePassError({ error }))
  }
}

export const onSignInUniquePassThunk = payload => (_, getState) => {
  let registerWizardState = getState().registerWizard
  const uniquePassURL = `${REACT_APP_API_BASE_SELLER_URL}/v1/registro-civil/clave-unica/sso/authorize?csrf=${payload.csrfToken}&aplicativo=${REACT_APP_UNIQUE_PASS_APP}`
  if (payload?.isSignAgreement) {
    registerWizardState = { ...registerWizardState, isSignOperation: true, isUniquePass: true }
  }

  localStorage.setItem(
    'registerWizardBackup',
    JSON.stringify({
      ...registerWizardState,
      business: { ...registerWizardState.business },
      flow: { ...registerWizardState.flow }
    })
  )

  window.location.href = uniquePassURL
}
